/**
 * Call Analytics — Not Answered aging (display-only).
 *
 * Window: 7 distinct IST calendar days with ≥1 outbound Not Answered call.
 * On day 8+ hide that candidate from History + KPI NA / Missed / attention.
 *   - Day 8 = today is after the 7th NA day, or the loaded set already has
 *     7 NA days (last-7 including today: 7 NA days in that set = suppress today).
 * An Answered/completed call in the loaded set lifts suppression.
 * Does not delete ATS candidates or change pipeline status.
 *
 * UI “Not Answered” (same family as MyCalls / attention widgets):
 *   noanswer, noanswered, no-answer, no_answer, not answered, notanswered,
 *   not_answered, not_rang, not connected, missed.
 * Busy / Failed are not NA. Only outbound NA days count.
 */

export const NA_AGING_DISTINCT_DAYS = 7

const IST_DAY_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** Statuses the Call Analytics UI already treats as Not Answered. */
const NA_STATUSES = new Set([
  'no-answer', 'no_answer', 'noanswered', 'noanswer', 'not answered', 'notanswered',
  'not_answered', 'not_rang', 'not-rang', 'not connected', 'not-connected', 'notconnected',
  'missed',
])

const ANSWERED_STATUSES = new Set(['answered', 'completed'])

function isEmptyId(value) {
  if (value == null || value === false) return true
  const s = String(value).trim()
  return !s || s === '0' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined'
}

function statusKey(call) {
  if (!call || typeof call !== 'object') return ''
  return String(call._rawStatus || call.status || call.call_status || '').toLowerCase().trim()
}

function directionKey(call) {
  if (!call || typeof call !== 'object') return ''
  return String(call._rawDirection || call.direction || '').toLowerCase()
}

function isInboundCall(call) {
  const d = directionKey(call)
  return d.includes('inbound') || d === 'incoming'
}

function parseIstDate(str) {
  if (!str) return null
  let s = String(str).trim().replace(/^(\d{4}-\d{2}-\d{2})\s(\d{2}:)/, '$1T$2')
  s = s.replace(/(\d)\s+([+-]\d{4})$/, '$1$2')
  s = s.replace(/([+-])(\d{2})(\d{2})$/, '$1$2:$3')
  if (/[Zz]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) return new Date(s)
  const d = new Date(`${s}Z`)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Last-10-digit Indian mobile, same idea as Call Analytics KPI unique-phone keys. */
export function naAgingNormalizePhone(raw) {
  if (raw == null || raw === '' || raw === '—') return ''
  let digits = String(raw).replace(/\D/g, '')
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2)
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1)
  if (!digits) return ''
  return digits.length >= 10 ? digits.slice(-10) : digits
}

/**
 * Candidate identity: candidate_id first, else normalized phone.
 * @returns {string|null} `c:{id}` or `p:{phone}`
 */
export function naAgingCandidateKey(call) {
  if (!call || typeof call !== 'object') return null
  const id = call.candidateId != null && call.candidateId !== ''
    ? call.candidateId
    : call.candidate_id
  if (!isEmptyId(id)) return `c:${String(id).trim()}`
  const phone = naAgingNormalizePhone(call.phone || call.contactNo || call.contact_no)
  return phone ? `p:${phone}` : null
}

export function getIstTodayStr(now = new Date()) {
  try {
    return IST_DAY_FMT.format(now)
  } catch (e) {
    return now.toISOString().slice(0, 10)
  }
}

/** Asia/Kolkata YYYY-MM-DD for a mapped or attention call row. */
export function callIstDay(call) {
  if (!call || typeof call !== 'object') return ''
  if (call._istDay) return String(call._istDay)
  const raw = call._rawDate
    || call.created_at
    || call.date_created
    || call.createdAt
    || call.call_time
    || call.start_time
    || call.StartTime
    || call.dateCreated
    || call.DateCreated
    || call.call_date
    || call.updated_at
    || null
  const d = parseIstDate(raw)
  if (!d || Number.isNaN(d.getTime())) return ''
  try {
    return IST_DAY_FMT.format(d)
  } catch (e) {
    return ''
  }
}

/** Outbound Not Answered — matches MyCalls KPI / attention NA, not Busy. */
export function isOutboundNotAnsweredCall(call) {
  if (!call || typeof call !== 'object') return false
  if (isInboundCall(call)) return false
  return NA_STATUSES.has(statusKey(call))
}

/** Answered / completed (any direction) — lifts NA aging suppression. */
export function isAnsweredCallForNaAging(call) {
  if (!call || typeof call !== 'object') return false
  return ANSWERED_STATUSES.has(statusKey(call))
}

/**
 * Keys of candidates who completed 7 IST NA days with no Answer in the loaded set.
 * Uses only the rows passed in — does not fetch extra history.
 *
 * @param {Array} calls
 * @param {{ today?: string }} [options] today = IST YYYY-MM-DD
 * @returns {Set<string>}
 */
export function buildNaAgingSuppressedKeys(calls, _options = {}) {
  const list = Array.isArray(calls) ? calls : []
  // options.today (IST YYYY-MM-DD) is accepted for callers/tests. 7 NA days
  // in the loaded set is enough to suppress — no extra history drain.
  const byKey = new Map()

  list.forEach((call) => {
    const key = naAgingCandidateKey(call)
    if (!key) return
    let entry = byKey.get(key)
    if (!entry) {
      entry = { naDays: new Set(), answered: false }
      byKey.set(key, entry)
    }
    if (isAnsweredCallForNaAging(call)) {
      entry.answered = true
      return
    }
    if (!isOutboundNotAnsweredCall(call)) return
    const day = callIstDay(call)
    if (day) entry.naDays.add(day)
  })

  const suppressed = new Set()
  byKey.forEach((entry, key) => {
    if (entry.answered) return
    if (entry.naDays.size < NA_AGING_DISTINCT_DAYS) return
    // 7 distinct NA days in the loaded set completes the window.
    // Day 8+ = today after the 7th NA day, or last-7 including today with
    // 7 NA days already in-range — hide until they get an Answered call.
    suppressed.add(key)
  })
  return suppressed
}

export function excludeNaAgedCandidates(calls, suppressedKeys) {
  const list = Array.isArray(calls) ? calls : []
  if (!suppressedKeys || !suppressedKeys.size) return list
  return list.filter((call) => {
    const key = naAgingCandidateKey(call)
    if (!key) return true
    if (!suppressedKeys.has(key)) return true
    // Inbound missed (incl. Start Calling OFF / not-in-ATS) must stay visible
    // even when Direction is empty and only Status=not_rang / skip is set.
    if (isInboundCall(call)) return true
    if (call._callsAvailableSkip) return true
    const status = statusKey(call)
    if (
      status === 'not_rang'
      || status === 'not-rang'
      || status === 'notrang'
      || status === 'missed'
      || status === 'missed-call'
      || status === 'not-connected'
      || status === 'not_connected'
    ) return true
    return false
  })
}

export function applyNaAgingToCalls(calls, options = {}) {
  const keys = options.suppressedKeys || buildNaAgingSuppressedKeys(calls, options)
  return excludeNaAgedCandidates(calls, keys)
}

export default {
  NA_AGING_DISTINCT_DAYS,
  naAgingNormalizePhone,
  naAgingCandidateKey,
  getIstTodayStr,
  callIstDay,
  isOutboundNotAnsweredCall,
  isAnsweredCallForNaAging,
  buildNaAgingSuppressedKeys,
  excludeNaAgedCandidates,
  applyNaAgingToCalls,
}
